import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

const HYPERMED_STELLAIR_PROXY = '/stellair/proxy';

interface KeyValueInterface {
  key: string;
  value: string;
}

export interface StellairApiCallQueryParam extends KeyValueInterface {
}

export interface StellairApiCallHeaderParam extends KeyValueInterface {
}

export enum StellairApiCallMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export interface StellairApiCallInfos<T> {
  url: string;
  method: StellairApiCallMethod;
  body?: T;
  headers?: Array<StellairApiCallHeaderParam>;
  query?: Array<StellairApiCallQueryParam>;
  pathParams?: Map<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class StellairApiService {

  constructor(
    private _http: HttpClient,
  ) { }

  public async callStellairApi<T, R>(params: StellairApiCallInfos<T>, accessToken: string): Promise<R> {
    try {
      let url = `${environment.basePath}${HYPERMED_STELLAIR_PROXY}`;
      if (params.url) {
        const tmpUrl = this._buildUrl(params.url, params.pathParams);
        const b64Path = btoa(tmpUrl);
        url += `/${b64Path}`;
      }

      let query = '';
      if (params.query) {
        query = params.query.map((param: StellairApiCallQueryParam) => `${param.key}=${param.value}`).join('&');
      }

      if (query) {
        url += `?${query}`;
      }

      let headers: HttpHeaders = new HttpHeaders();

      if (accessToken) {
        headers = headers.append('x-stellair-access-token', accessToken);
      }

      if (params.headers) {
        params.headers.forEach((header: StellairApiCallHeaderParam) => {
          headers = headers.append(header.key, header.value);
        });
      }

      if (params.method === StellairApiCallMethod.POST || params.method === StellairApiCallMethod.PUT) {
        headers = headers.append('Content-Type', 'application/json');
      }

      const response = await this._http.request<R>(params.method, url, {
        body: params.body,
        headers,
      }).toPromise();

      return response;
    } catch (error) {
      if (error.error?.message) {
        throw error.error.message;
      } else {
        throw error;
      }
    }
  }

  private _buildUrl(url: string, pathParams?: Map<string, string>): string {
    if (pathParams) {
      url = url.replace(/\{([^}]+)\}/g, (match, key) => {
        // Enlève les accolades et récupère la valeur dans la map
        const newKey = key.replace('{', '').replace('}', '');
        return pathParams.get(newKey);
      });
    }

    return url;
  }
}

export enum StellairApiInfosMapKey {
  listTerminaux = 'listTerminaux',
  healthcareToken = 'healthcareToken',
  readVital = 'readVital',
  createUpdatePatient = 'createUpdatePatient',
  getFichePatientInfos = 'getFichePatientInfos',
}

export const StellairApiInfosMap = new Map<StellairApiInfosMapKey, StellairApiCallInfos<any>>([
  [StellairApiInfosMapKey.listTerminaux, {
    url: '/v1/terminals',
    method: StellairApiCallMethod.GET,
  }],
  [StellairApiInfosMapKey.healthcareToken, {
    url: '/v1/cps/token',
    method: StellairApiCallMethod.POST,
  }],
  [StellairApiInfosMapKey.readVital, {
    url: '/v1/patient/vitale',
    method: StellairApiCallMethod.GET,
  }],
  [StellairApiInfosMapKey.createUpdatePatient, {
    url: '/v1/patient/manual',
    method: StellairApiCallMethod.POST,
  }],
  [StellairApiInfosMapKey.getFichePatientInfos, {
    url: '/v1/patient/{id}/update',
    method: StellairApiCallMethod.GET,
  }],
]);
